import { Card } from '../../../molecules/Card/Card';
import {
  isEmpty,
  type ChartBaseProps,
  type ChartRow,
} from '../../../molecules/ChartFrame/chartProps';
import { legendItems, type ChartSeries } from '../../../molecules/ChartFrame/chartTheme';
import { ChartLegend } from '../../../molecules/ChartLegend/ChartLegend';
import { KPI, type KPIMetric } from '../../../molecules/KPI/KPI';
import { BarChart } from '../../../organisms/BarChart/BarChart';
import {
  PLOT_HEIGHT,
  cardState,
  spanClass,
  ticksFor,
  type WidgetBlockProps,
} from '../shared/cardShell';

export type EngagementVolumeView = {
  /** `Total engagements` and `Engagement rate`. */
  kpis: KPIMetric[];
  rows: ChartRow[];
  xKey: string;
  series: ChartSeries[];
  /** Whether any mention is in scope — an empty scope is empty even if buckets exist. */
  hasRows: boolean;
  formatValue: (value: number) => string;
  /** Comparison-period reading per row and series; nothing when comparison is off. */
  formatCompare?: ChartBaseProps['formatCompare'];
};

export type EngagementVolumeCardProps = WidgetBlockProps & {
  view?: EngagementVolumeView;
  /** Opens the conversations behind a bar (index into `rows`). */
  onViewDetails?: (index: number) => void;
};

/** Figma `849:29599` — total interactions per bucket. */
export function EngagementVolumeCard({
  status,
  view,
  span,
  chrome,
  onRetry,
  onViewDetails,
}: EngagementVolumeCardProps) {
  const hasData = view !== undefined && view.hasRows && !isEmpty(view.rows, view.series);
  return (
    <Card
      {...chrome}
      className={spanClass(span)}
      {...cardState(status, hasData, onRetry)}
      title="Engagement volume over time"
      helpText="Shows how audience interactions change over time across content published by your brand and content published about it by others."
      kpi={view && <KPI metrics={view.kpis} />}
      chart={
        view && (
          <BarChart
            data={view.rows}
            xKey={view.xKey}
            onViewDetails={onViewDetails}
            series={view.series}
            height={PLOT_HEIGHT}
            legend="none"
            xTicksTarget={ticksFor(span)}
            formatValue={view.formatValue}
            formatCompare={view.formatCompare}
          />
        )
      }
      legend={view && <ChartLegend items={legendItems(view.series)} />}
    />
  );
}
